import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, AlertCircle, Loader2, Settings, Database, Trash2 } from 'lucide-react';
import { Client } from '@/types/client';
import { useToast } from '@/hooks/use-toast';
import api from '@/lib/api';
import { useConfirmation } from '@/hooks/useConfirmation';

interface SouthV0IntegrationProps {
  client: Client;
}

interface SouthV0Config {
  system_token: string;
  api_base_url: string;
}

interface SouthV0IntegrationStatus {
  active: boolean;
  has_token: boolean;
  has_api_url: boolean;
  system_management: string;
}

interface ApiResponse<T> {
  data: T;
}

interface TestConnectionResponse {
  success: boolean;
  error?: string;
}

const SouthV0Integration: React.FC<SouthV0IntegrationProps> = ({ client }) => {
  const [integrationStatus, setIntegrationStatus] = useState<SouthV0IntegrationStatus>({
    active: false,
    has_token: false,
    has_api_url: false,
    system_management: ''
  });
  
  const { confirm, ConfirmationDialog } = useConfirmation();

  const [config, setConfig] = useState<SouthV0Config>({
    system_token: '',
    api_base_url: ''
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  const { toast } = useToast();

  // Verificar status da integração ao carregar
  useEffect(() => {
    console.log('🔄 [SouthV0Integration] useEffect triggered for client:', client.id);
    checkIntegrationStatus();
  }, [client.id]);

  // Log quando integrationStatus muda
  useEffect(() => {
    console.log('📊 [SouthV0Integration] integrationStatus changed:', integrationStatus);
  }, [integrationStatus]);

  const checkIntegrationStatus = async () => {
    console.log('🔍 [SouthV0Integration] checkIntegrationStatus called for client:', client.id);
    setLoading(true);
    try {
      const response = await api.get(`/south/check-integration?client_id=${client.id}`);
      const responseData = (response as ApiResponse<SouthV0IntegrationStatus>).data;
      console.log('✅ [SouthV0Integration] API response:', responseData);
      console.log('📊 [SouthV0Integration] Setting integrationStatus to:', responseData);
      setIntegrationStatus(responseData);
      
      // Buscar dados atualizados do cliente
      const clientResponse = await api.get(`/clients/${client.id}`);
      const updatedClient = (clientResponse as ApiResponse<Client>).data;
      console.log('🔄 [SouthV0Integration] Updated client data:', updatedClient);
      
      // Carregar configuração atual se existir
      if (updatedClient.system_token || updatedClient.api_base_url) {
        setConfig({
          system_token: updatedClient.system_token || '',
          api_base_url: updatedClient.api_base_url || ''
        });
      }
    } catch (error) {
      console.error('❌ [SouthV0Integration] Erro ao verificar status:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível verificar o status da integração South V0',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config.system_token.trim() || !config.api_base_url.trim()) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Token e URL da API são obrigatórios',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      console.log('💾 [SouthV0Integration] Saving configuration for client:', client.id, 'with data:', config);
      await api.put(`/clients/${client.id}`, {
        system_management: 'south-v0',
        system_token: config.system_token,
        api_base_url: config.api_base_url
      });

      toast({
        title: 'Sucesso',
        description: 'Configuração South V0 salva com sucesso!'
      });

      console.log('🔄 [SouthV0Integration] Calling checkIntegrationStatus after save...');
      // Verificar status novamente
      await checkIntegrationStatus();
    } catch (error) {
      console.error('❌ [SouthV0Integration] Erro ao salvar configuração:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível salvar a configuração South V0',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    const confirmed = await confirm({
      title: 'Limpar Configuração South V0',
      description: 'Tem certeza que deseja limpar a configuração South V0? Esta ação não pode ser desfeita.'
    });

    if (!confirmed) return;

    setClearing(true);
    try {
      await api.put(`/clients/${client.id}`, {
        system_token: '',
        api_base_url: ''
      });

      setConfig({
        system_token: '',
        api_base_url: ''
      });

      toast({
        title: 'Sucesso',
        description: 'Configuração South V0 limpa com sucesso!'
      });

      // Verificar status novamente
      await checkIntegrationStatus();
    } catch (error) {
      console.error('Erro ao limpar configuração South V0:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível limpar a configuração South V0',
        variant: 'destructive'
      });
    } finally {
      setClearing(false);
    }
  };

  const handleTestConnection = async () => {
    if (!config.system_token.trim() || !config.api_base_url.trim()) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Token e URL da API são obrigatórios para testar a conexão',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      // Temporariamente salvar a configuração para teste
      await api.put(`/clients/${client.id}`, {
        system_token: config.system_token,
        api_base_url: config.api_base_url
      });

      // Testar conexão
      const response = await api.post('/south/test-connection', {
        client_id: client.id
      });

      const responseData = (response as ApiResponse<TestConnectionResponse>).data;
      if (responseData.success) {
        toast({
          title: 'Conexão bem-sucedida!',
          description: 'A conexão com a API South V0 foi estabelecida com sucesso.'
        });
      } else {
        toast({
          title: 'Erro na conexão',
          description: responseData.error || 'Não foi possível conectar com a API South V0',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Erro ao testar conexão South V0:', error);
      toast({
        title: 'Erro na conexão',
        description: 'Não foi possível testar a conexão com a API South V0',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            <span>Verificando integração South V0...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  console.log('🎨 [SouthV0Integration] Rendering with integrationStatus.active:', integrationStatus.active);
  
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-600" />
            Integração South V0
            <Badge variant={integrationStatus.active ? 'default' : 'secondary'}>
              {integrationStatus.active ? 'Ativa' : 'Inativa'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status da Integração */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              {integrationStatus.has_token ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600" />
              )}
              <span className="text-sm">Token configurado</span>
            </div>
            <div className="flex items-center gap-2">
              {integrationStatus.has_api_url ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600" />
              )}
              <span className="text-sm">URL da API configurada</span>
            </div>
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-600" />
              <span className="text-sm">Sistema: {integrationStatus.system_management}</span>
            </div>
          </div>

          {/* Configuração */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="system_token">Token do Sistema South V0</Label>
              <Input
                id="system_token"
                type="password"
                value={config.system_token}
                onChange={(e) => setConfig({ ...config, system_token: e.target.value })}
                placeholder="Digite o token de autenticação"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="api_base_url">URL Base da API South V0</Label>
              <Input
                id="api_base_url"
                value={config.api_base_url}
                onChange={(e) => setConfig({ ...config, api_base_url: e.target.value })}
                placeholder="https://service-provisorio.premiumclube.org.br"
                className="mt-1"
              />
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handleTestConnection}
              disabled={loading || !config.system_token.trim() || !config.api_base_url.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Database className="w-4 h-4 mr-2" />
              )}
              Testar Conexão
            </Button>

            <Button
              onClick={handleSave}
              disabled={saving || !config.system_token.trim() || !config.api_base_url.trim()}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Salvar Configuração
            </Button>

            {(client.system_token || client.api_base_url) && (
              <Button
                onClick={handleClear}
                disabled={clearing}
                variant="destructive"
                className="bg-red-600 hover:bg-red-700"
              >
                {clearing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Limpar Configuração
              </Button>
            )}
          </div>

          {/* Informações sobre South V0 */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <h4 className="font-medium text-blue-900 mb-2">Sobre o South V0</h4>
            <ul className="text-blue-800 text-sm space-y-1">
              <li>• <strong>Endpoint:</strong> /boletos/lista (diferente do South padrão)</li>
              <li>• <strong>Estrutura:</strong> Dados em objeto com chaves numéricas</li>
              <li>• <strong>Campos extras:</strong> FaturasCodigoBarras, FaturasFormaPagamento</li>
              <li>• <strong>Compatibilidade:</strong> Totalmente compatível com o sistema de disparos</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <ConfirmationDialog />
    </>
  );
};

export default SouthV0Integration;
